<template>
  <div class="pa-4">
    <v-skeleton-loader v-if="loading" class="mx-auto" type="card"></v-skeleton-loader>

    <div v-else-if="paymentsReady">
      <v-card outlined class="pa-4 text-center">
        <v-icon color="success" size="48" class="mb-2">mdi-check-circle-outline</v-icon>
        <div class="text-h6 mb-1">{{ $t('message.payments_connected') }}</div>
        <div class="text-body-2 mb-1">
          <v-chip small label color="primary" class="mr-1">{{ paymentsModule }}</v-chip>
          {{ $t('message.connected_using') }}
          <strong>{{ moduleDefinition(paymentsModule).name }}</strong>
        </div>
        <div class="mb-4 d-flex justify-center">
          <img v-if="moduleDefinition(paymentsModule).logo" :src="moduleDefinition(paymentsModule).logo"
            class="module-card__logo" />
        </div>
        <v-btn outlined small color="primary" :href="moduleDefinition(paymentsModule).url" target="_blank" class="mr-2">
          {{ $t('message.go_to_website') }}
        </v-btn>
        <v-btn outlined small :href="learnMoreUrl(paymentsModule)" target="_blank">
          {{ $t('message.documentation') }}
        </v-btn>
      </v-card>
    </div>

    <div v-else>
      <div class="text-h6 mb-1">{{ $t('message.payments_soon') }}</div>
      <div class="text-body-2 grey--text mb-4">{{ $t('message.select_payment_module') }}</div>
      <v-row>
        <v-col v-for="moduleId in paymentOptions" :key="moduleId" cols="12" sm="6" md="4">
          <v-card v-if="moduleDefinition(moduleId)" outlined class="module-card d-flex flex-column">
            <div class="module-card__header pa-4 d-flex align-center justify-center">
              <img v-if="moduleDefinition(moduleId).logo" :src="moduleDefinition(moduleId).logo"
                class="module-card__logo" />
              <span v-else class="text-subtitle-1 font-weight-bold">{{ moduleDefinition(moduleId).name }}</span>
            </div>

            <div class="module-card__body pa-4 flex-grow-1 overflow-y-auto" v-html="moduleDefinition(moduleId).html">
            </div>

            <v-divider></v-divider>
            <v-card-actions class="pa-3">
              <v-btn small elevation="0" color="primary" @click="connectModule(moduleId)">
                {{ $t('message.connect_payments') }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn small text :href="moduleDefinition(moduleId).url" target="_blank">
                {{ $t('message.go_to_website') }}
              </v-btn>
              <v-btn small text :href="learnMoreUrl(moduleId)" target="_blank">
                {{ $t('message.documentation') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import Api from "@/services/Api";
import { Environment } from "@/services/Environment";

const MODULES = {
  stripe: {
    name: "Stripe",
    url: "https://stripe.com",
    logo: undefined,
    html: `
      <div class="text-subtitle-2 font-weight-bold mb-1">Stripe Payments</div>
      <div class="text-body-2 mb-2">Accept credit cards, debit cards, and more. Stripe is available in 46+ countries with industry-leading fraud protection.</div>
      <ul class="text-body-2 pl-4">
        <li>2.9% + 30¢ per successful card charge</li>
        <li>Instant payouts available</li>
        <li>No setup or monthly fees</li>
        <li>Supports subscriptions &amp; one-time payments</li>
      </ul>
    `,
  },
  infinicept: {
    name: "LaunchPay (Infinicept)",
    url: "https://www.infinicept.com/launchpay/",
    logo: undefined,
    html: `
      <div class="text-subtitle-2 font-weight-bold mb-1">LaunchPay by Infinicept</div>
      <div class="text-body-2 mb-2">A fully integrated payment solution built for fitness businesses. Competitive rates with dedicated support.</div>
      <ul class="text-body-2 pl-4">
        <li>Competitive processing rates</li>
        <li>Supports ACH/bank transfers</li>
        <li>Dedicated onboarding support</li>
        <li>Supports subscriptions &amp; one-time payments</li>
      </ul>
    `,
  },
};

export default {
  data() {
    return {
      loading: true,
      paymentOptions: [],
      paymentsReady: false,
      paymentsModule: null,
      stripe_client_id: Environment.get("VUE_APP_STRIPE_CLIENT_ID"),
      stripe_state: null,
      modules: Object.fromEntries(
        Object.entries(MODULES).map(([id, def]) => [id, { ...def }])
      ),
    };
  },
  created() {
    this.loadPage();
  },
  methods: {
    loadPage() {
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/is_connected/all?load-modules=true"
        )
        .then((res) => {
          this.stripe_state = res.data.stripeState;
          if (res.data.availablePaymentModules) {
            this.paymentOptions = res.data.availablePaymentModules;
            for (const p of this.paymentOptions) {
              if (this.modules[p]) {
                try {
                  this.modules[p].logo = require('@/assets/logos/payments/' + p + '.png');
                } catch (e) { /* no logo file for this module */ }
              }
            }
          }
          if (res.data.paymentsReady) this.paymentsReady = res.data.paymentsReady;
          if (res.data.paymentsModule) {
            this.paymentsModule = res.data.paymentsModule;
            if (this.modules[res.data.paymentsModule] && !this.modules[res.data.paymentsModule].logo) {
              try {
                this.modules[res.data.paymentsModule].logo = require('@/assets/logos/payments/' + res.data.paymentsModule + '.png');
              } catch (e) { /* no logo file */ }
            }
          }
          this.loading = false;

        })
        .catch((err) => {
          if (err) this.showAlert("error", this.$t("message.oops_not_working"));
          console.log(err)
          this.loading = false;
        });
    },
    connectPayments() {
      const url =
        "https://connect.stripe.com/oauth/authorize?" +
        "client_id=" +
        this.stripe_client_id +
        "&state=" +
        this.stripe_state +
        "&scope=read_write&response_type=code" +
        "&stripe_user[email]=" +
        this.academy.email.toLowerCase().trim() + '&redirect_uri=https://admin.bjjlink.com/secure/transactions';
      window.location = url;
    },
    moduleDefinition(id) {
      return this.modules[id] || null;
    },
    learnMoreUrl(id) {
      return "https://bjjlink.tawk.help/article/bjjlink-available-payment-modules#" + (id || "");
    },
    connectModule(id) {
      // Actions wired per module by caller
      if (id === "stripe") {
        // TODO: add stripe connect action
      } else if (id === "infinicept") {
        // TODO: navigate to infinicept onboarding
      }
    },
  },
};
</script>

<style scoped>
.module-card {
  min-height: 320px;
}

.module-card__header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  min-height: 80px;
}

.module-card__logo {
  max-height: 56px;
  max-width: 180px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.module-card__body {
  max-height: 180px;
}
</style>
